'use client';

/**
 * SearchForm — name + city + state + optional BCI.
 *
 * City uses a <datalist>-backed combobox off DISTRICT_TO_STATE (lowercase
 * district names). When the user picks a city we auto-fill the state; the
 * select is still editable for edge cases (e.g., "chandigarh" that maps to
 * a UT, or districts with multiple possible states).
 *
 * Form shape matches the /api/advocate-search body. The parent owns the
 * streaming lifecycle — this component only captures + validates inputs.
 */

import { useEffect, useMemo, useState } from 'react';
import { DISTRICT_TO_STATE } from '@/lib/smart-finder/district-data';

export interface SearchFormValues {
  name: string;
  city: string;
  state: string;
  barCouncilId?: string;
  source?: string;
}

interface SearchFormProps {
  initial?: Partial<SearchFormValues>;
  disabled?: boolean;
  onSubmit: (values: SearchFormValues) => void;
}

// Unique sorted list of states from DISTRICT_TO_STATE. Keep the display-cased
// strings (e.g., "Uttar Pradesh") exactly as stored — server lowercases at
// the API boundary so casing here is purely cosmetic.
const STATES: string[] = (() => {
  const set = new Set<string>();
  for (const v of Object.values(DISTRICT_TO_STATE)) {
    if (Array.isArray(v)) for (const s of v) set.add(s);
    else set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
})();

function toTitleCase(s: string): string {
  return s.replace(/\b\w/g, c => c.toUpperCase());
}

function stateForCity(city: string): string | null {
  const mapped = DISTRICT_TO_STATE[city.toLowerCase().trim()];
  if (!mapped) return null;
  return Array.isArray(mapped) ? mapped[0] : mapped;
}

export default function SearchForm({ initial, disabled = false, onSubmit }: SearchFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [bci, setBci] = useState(initial?.barCouncilId ?? '');
  const [stateTouched, setStateTouched] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Auto-populate state from city unless the user has manually picked one.
  useEffect(() => {
    if (stateTouched) return;
    const auto = stateForCity(city);
    if (auto) setState(auto);
  }, [city, stateTouched]);

  // Memoised city option list — trimmed to ~40 by prefix+substring match.
  const cityOptions = useMemo(() => {
    const q = city.toLowerCase().trim();
    const all = Object.keys(DISTRICT_TO_STATE);
    if (!q) return all.slice(0, 40);
    const prefix: string[] = [];
    const contains: string[] = [];
    for (const k of all) {
      if (k.startsWith(q)) prefix.push(k);
      else if (k.includes(q)) contains.push(k);
    }
    return [...prefix, ...contains].slice(0, 40);
  }, [city]);

  const nameTokens = name.trim().split(/\s+/).filter(Boolean);
  const errors = {
    name: name.trim().length < 2
      ? 'Please enter your full name.'
      : nameTokens.length < 2
        ? 'Please include at least your first and last name.'
        : null,
    city: !city.trim() ? 'Pick the city where you mostly practise.' : null,
    state: !state.trim() ? 'Select your state.' : null,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setAttemptedSubmit(true);
    if (hasErrors) return;
    onSubmit({
      name: name.trim().replace(/\s+/g, ' '),
      city: city.trim().toLowerCase(),
      state: state.trim().toLowerCase(),
      barCouncilId: bci.trim() || undefined,
      source: initial?.source,
    });
  }

  const showError = (key: keyof typeof errors) => attemptedSubmit && errors[key];

  const inputBase =
    'w-full rounded-lg border border-line bg-white px-4 py-3 text-ink placeholder:text-ink-5 ' +
    'focus:outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-200 ' +
    'disabled:bg-paper-sunken disabled:text-ink-4 transition';

  const labelBase = 'block text-sm font-medium text-ink-2 mb-1.5';
  const errorBase = 'mt-1 text-xs text-red-700';

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <div>
        <label htmlFor="adv-name" className={labelBase}>
          Your full name
        </label>
        <input
          id="adv-name"
          type="text"
          autoComplete="name"
          maxLength={100}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Vikrant Sharma"
          disabled={disabled}
          className={inputBase}
          aria-invalid={showError('name') ? 'true' : 'false'}
          aria-describedby={showError('name') ? 'adv-name-error' : undefined}
          required
        />
        {showError('name') && (
          <p id="adv-name-error" className={errorBase}>{errors.name}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="adv-city" className={labelBase}>
            City
          </label>
          <input
            id="adv-city"
            type="text"
            list="adv-city-list"
            autoComplete="off"
            maxLength={60}
            value={city}
            onChange={e => setCity(e.target.value)}
            placeholder="e.g. delhi"
            disabled={disabled}
            className={inputBase}
            aria-invalid={showError('city') ? 'true' : 'false'}
            aria-describedby={showError('city') ? 'adv-city-error' : undefined}
            required
          />
          <datalist id="adv-city-list">
            {cityOptions.map(opt => (
              <option key={opt} value={opt}>{toTitleCase(opt)}</option>
            ))}
          </datalist>
          {showError('city') && (
            <p id="adv-city-error" className={errorBase}>{errors.city}</p>
          )}
        </div>

        <div>
          <label htmlFor="adv-state" className={labelBase}>
            State
          </label>
          <select
            id="adv-state"
            value={state}
            onChange={e => { setState(e.target.value); setStateTouched(true); }}
            disabled={disabled}
            className={inputBase + ' appearance-none'}
            aria-invalid={showError('state') ? 'true' : 'false'}
            aria-describedby={showError('state') ? 'adv-state-error' : undefined}
            required
          >
            <option value="">Select a state</option>
            {STATES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {showError('state') && (
            <p id="adv-state-error" className={errorBase}>{errors.state}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="adv-bci" className={labelBase}>
          Bar Council ID <span className="text-ink-4 font-normal">— optional</span>
        </label>
        <input
          id="adv-bci"
          type="text"
          maxLength={40}
          value={bci}
          onChange={e => setBci(e.target.value)}
          placeholder="e.g. D/1234/2018"
          disabled={disabled}
          className={inputBase}
        />
        <p className="mt-1 text-xs text-ink-4">
          Stored on your profile. Not used to match your cases.
        </p>
      </div>

      <button
        type="submit"
        disabled={disabled}
        className="w-full rounded-lg bg-primary px-5 py-3 text-base font-medium text-white
                   shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2
                   focus:ring-primary-300 focus:ring-offset-2 disabled:bg-ink-5
                   disabled:cursor-not-allowed transition"
      >
        {disabled ? 'Searching your cases…' : 'Find my cases'}
      </button>
    </form>
  );
}
